import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PrintInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useApp();
  const { invoices, settings } = state;
  const printRef = useRef();

  const invoice = invoices.find(inv => inv.id === parseInt(id));

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Invoice not found</p>
        <button onClick={() => navigate('/invoice-history')} className="btn-primary mt-4">
          Back to Invoice History
        </button>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element);
    const imgData = canvas.toDataURL('image/png');
    
    const pdf = new jsPDF();
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    pdf.save(`invoice-${invoice.id}.pdf`);
  };

  return (
    <div>
      {/* Action Bar */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <button
          onClick={() => navigate('/invoice-history')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Invoice History
        </button>
        <div className="flex space-x-4">
          <button onClick={handlePrint} className="btn-secondary">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </button>
          <button onClick={handleDownloadPDF} className="btn-primary">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Invoice Content */}
      <div ref={printRef} className="bg-white p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b-2 border-gray-200 pb-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{settings.shopName}</h1>
              <div className="mt-2 text-gray-600">
                <p>{settings.email}</p>
                <p>{settings.mobile}</p>
                <p>{settings.website}</p>
                <p>{settings.address}</p>
                {settings.gstNumber && <p>GST: {settings.gstNumber}</p>}
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-bold text-gray-900">INVOICE</h2>
              <p className="text-gray-600">Invoice #: INV-{invoice.id}</p>
              <p className="text-gray-600">
                Date: {new Date(invoice.date || invoice.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Bill To:</h3>
          <div className="text-gray-700">
            <p className="font-medium">{invoice.customerName}</p>
            <p>{invoice.customerMobile}</p>
            {invoice.customerEmail && <p>{invoice.customerEmail}</p>}
          </div>
        </div>

        {/* Work Details Table */}
        <div className="mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left">Work Description</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Quantity</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Rate</th>
                <th className="border border-gray-300 px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.workRows.map((row, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-2">{row.workName}</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">{row.quantity}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">₹{parseFloat(row.pricePerUnit).toFixed(2)}</td>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    ₹{(parseFloat(row.quantity) * parseFloat(row.pricePerUnit)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Payment Summary */}
        <div className="flex justify-end mb-6">
          <div className="w-64">
            <div className="flex justify-between py-2 border-b">
              <span className="font-medium">Subtotal:</span>
              <span>₹{(parseFloat(invoice.subtotal) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="font-medium">Paid Amount:</span>
              <span>₹{(parseFloat(invoice.paidAmount) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 text-lg font-bold">
              <span>Remaining Amount:</span>
              <span className={(parseFloat(invoice.remainingAmount) || 0) > 0 ? 'text-red-600' : 'text-green-600'}>
                ₹{(parseFloat(invoice.remainingAmount) || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment QR Code and Message */}
        {(parseFloat(invoice.remainingAmount) || 0) > 0 && settings.paymentBarcode && (
          <div className="mb-6 text-center border-t pt-6">
            <p className="text-lg font-medium text-gray-900 mb-4">
              Please scan the QR code to pay the remaining amount
            </p>
            <img
              src={settings.paymentBarcode}
              alt="Payment QR Code"
              className="mx-auto w-32 h-32 object-contain"
            />
          </div>
        )}

        {/* Bank Details */}
        {(settings.accountNo || settings.ifscCode) && (
          <div className="mb-6 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Bank Details:</h3>
            <div className="text-gray-700">
              {settings.accountNo && <p>Account No: {settings.accountNo}</p>}
              {settings.ifscCode && <p>IFSC Code: {settings.ifscCode}</p>}
            </div>
          </div>
        )}

        {/* Signature */}
        {settings.signature && (
          <div className="flex justify-end mt-12">
            <div className="text-center">
              <img
                src={settings.signature}
                alt="Signature"
                className="w-32 h-16 object-contain mb-2"
              />
              <div className="border-t border-gray-400 pt-2">
                <p className="text-sm text-gray-600">Authorized Signature</p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t text-center text-gray-500 text-sm">
          <p>Thank you for your business!</p>
        </div>
      </div>
    </div>
  );
};

export default PrintInvoice;
